import React, { useState, useMemo } from 'react';
import { useDataQuery, useDataMutation } from '@dhis2/app-runtime';
import {
  Button,
  InputField,
  SingleSelect,
  SingleSelectOption,
  Chip,
  CircularLoader,
  AlertBar,
  IconAdd16,
  IconFullscreenExit16
} from '@dhis2/ui';
import { useOrgUnits } from '../hooks/useOrgUnits';
import { useNavigate} from 'react-router-dom';

// Constants
const ATTRIBUTE_IDS = {
  ORG_EMAIL: 'P1KhiQpaXO4',
  ORG_TYPE: 'WxaXz5yCf4f'
};

const ORG_TYPE_QUERY = {
  orgTypes: {
    resource: 'optionSets/z6JnM4OcF11',
    params: {
      fields: 'options[id,name,code]'
    }
  }
};

const CURRENT_USER_QUERY = {
  me: {
    resource: 'me',
    params: {
      fields: 'id,username,organisationUnits[id,name]'
    }
  }
};

// Static mutations
const MUTATIONS = {
  CREATE_ORG_UNIT: {
    resource: 'organisationUnits',
    type: 'create',
    data: ({ orgUnit }) => ({
      name: orgUnit.name.trim(),
      shortName: orgUnit.name.trim().substring(0, 50),
      openingDate: orgUnit.openingDate,
      level: orgUnit.level,
      parent: orgUnit.parent,
      attributeValues: orgUnit.attributeValues,
      code: orgUnit.code || generateOrgCode(orgUnit.name)
    }),
    params: {
      fields: 'id,name,level,code,created'
    }
  },
  DELETE_ORG_UNIT: {
    resource: 'organisationUnits',
    type: 'delete',
    id: ({ id }) => id
  },
  UPDATE_USER: {
    resource: 'users',
    type: 'update',
    id: ({ id }) => id,
    data: ({ data }) => data
  }
};

// Helper function for code generation
function generateOrgCode(name) {
  const initials = name.trim()
    .split(/\s+/)
    .map(word => word[0])
    .join('')
    .toUpperCase()
    .padEnd(3, 'X')
    .slice(0, 3);
  return `OU_${initials}_${Date.now().toString().slice(-4)}`;
}

const OrgUnitForm = () => {
  // Navigation and state
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(false);
  const [notification, setNotification] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [createdOrgUnitId, setCreatedOrgUnitId] = useState(null);
  const [formData, setFormData] = useState({
    continentId: '',
    countryId: '',
    regionId: '',
    organisationName: '',
    organisationType: '',
    orgEmail: '',
    departments: [],
    newDepartment: ''
  });
  
  // Data operations
  const [createOrgUnit] = useDataMutation(MUTATIONS.CREATE_ORG_UNIT);
  const [deleteOrgUnit] = useDataMutation(MUTATIONS.DELETE_ORG_UNIT);
  const [updateUser] = useDataMutation(MUTATIONS.UPDATE_USER, {
    onComplete: () => {
      console.log('User updated successfully');
      setNotification({
        success: true,
        message: 'Organization created successfully! Redirecting...'
      });
      setTimeout(() => navigate('/dashboard'), 2000);
    },
    onError: (error) => {
      console.error('User update failed:', error);
      setNotification({
        critical: true,
        message: `User update failed: ${error.message}`
      });
    }
  });

  // Data queries
  const { data: currentUserData, loading: userLoading } = useDataQuery(CURRENT_USER_QUERY);
  const { 
    getOrgUnitsByLevel, 
    getOrgUnitsByParent,
    loading: orgUnitsLoading,
   // error: orgUnitsError
  } = useOrgUnits();
  const { 
    data: orgTypeData, 
    loading: orgTypesLoading, 
   // error: orgTypesError 
  } = useDataQuery(ORG_TYPE_QUERY);

  // Memoized data
  const continent = useMemo(() => getOrgUnitsByLevel(2), [getOrgUnitsByLevel]);
  const countries = useMemo(() => getOrgUnitsByLevel(3), [getOrgUnitsByLevel]);
  const regions = useMemo(() => 
    formData.countryId ? getOrgUnitsByParent(formData.countryId) : [],
    [formData.countryId, getOrgUnitsByParent]
  );
  const orgTypes = useMemo(() => 
    orgTypeData?.orgTypes?.options || [], 
    [orgTypeData]
  );

 // Show loading state while checking user orgs
  if (userLoading) {
    return <CircularLoader />;
  }

   // If user has org units, they'll be redirected by the useEffect
  if (currentUserData?.me?.organisationUnits?.length > 0) {
    return null;
  }

  // Department management
  const addDepartment = () => {
    if (formData.newDepartment.trim()) {
      setFormData(prev => ({
        ...prev,
        departments: [...prev.departments, prev.newDepartment.trim()],
        newDepartment: ''
      }));
    }
  };

  const removeDepartment = (index) => {
    setFormData(prev => ({
      ...prev,
      departments: prev.departments.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setNotification(null);

    try {
      // Validate all required fields
      const requiredFields = {
        'Organization name': formData.organisationName,
        'Region': formData.regionId,
        'Organization type': formData.organisationType,
        'Organisation Email': formData.orgEmail,
      };

      const missingFields = Object.entries(requiredFields)
        .filter(([_, value]) => !(value || '').trim())
        .map(([name]) => name);

      if (missingFields.length > 0) {
        throw new Error(`Missing required fields: ${missingFields.join(', ')}`);
      }

      if (!currentUserData?.me?.id) {
        throw new Error("Could not determine current user information");
      }

      // 1. Create Main Organization Unit
      const orgPayload = {
        name: formData.organisationName.trim(),
        shortName: formData.organisationName.trim().substring(0, 50),
        openingDate: new Date().toISOString().split('T')[0],
        level: 4,
        parent: { id: formData.regionId },
        attributeValues: [
          {
            attribute: { id: ATTRIBUTE_IDS.ORG_TYPE },
            value: formData.organisationType
          },
          formData.orgEmail && {
            attribute: { id: ATTRIBUTE_IDS.ORG_EMAIL },
            value: formData.orgEmail.trim()
          }
        ].filter(Boolean)
      };

      const orgResponse = await createOrgUnit({ orgUnit: orgPayload });
      const newOrgUnitId = orgResponse.response?.uid || orgResponse.uid;

      if (!newOrgUnitId) {
        throw new Error("Organization Unit ID not returned from server");
      }

      setCreatedOrgUnitId(newOrgUnitId);

      // 2. Create Departments
      const createdDepartments = [];
      if (formData.departments.length > 0) {
        for (const deptName of formData.departments) {
          try {
            const deptResponse = await createOrgUnit({
              orgUnit: {
                name: deptName.trim(),
                shortName: deptName.trim().substring(0, 50),
                parent: { id: newOrgUnitId },
                openingDate: new Date().toISOString().split('T')[0],
                level: 5
              }
            });
            
            const deptId = deptResponse.response?.uid || deptResponse.uid;
            createdDepartments.push({
              id: deptId,
              name: deptName.trim()
            });
          } catch (deptError) {
            console.error(`Department creation failed for "${deptName}":`, deptError);
            throw new Error(`Failed to create department "${deptName}": ${deptError.message}`);
          }
        }
      }

      // 3. Assign ownership to current user
      await updateUser({
        id: currentUserData.me.id,
        data: {
          organisationUnits: [
            ...(currentUserData?.me?.organisationUnits || []),
            { 
              id: newOrgUnitId,
              name: formData.organisationName.trim(),
              access: { read: true, write: true, manage: true }
            },
            ...createdDepartments.map(dept => ({
              id: dept.id,
              name: dept.name,
              access: { read: true, write: true, manage: false }
            }))
          ],
          dataViewOrganisationUnits: [
            ...(currentUserData?.me?.dataViewOrganisationUnits || []),
            { id: newOrgUnitId },
            ...createdDepartments.map(dept => ({ id: dept.id }))
          ]
        }
      });

    } catch (error) {
      console.error("Submission failed:", error);
      setNotification({
        critical: true,
        message: error.message.includes('Failed to create department') 
          ? error.message 
          : `Creation failed: ${error.message}`
      });

      // Cleanup if org unit was created but process failed
      if (createdOrgUnitId) {
        try {
          await deleteOrgUnit({ id: createdOrgUnitId });
        } catch (rollbackError) {
          console.error('Rollback failed:', rollbackError);
        }
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Render methods
  const renderFormField = (label, children, required = true) => (
    <div style={{ marginBottom: '16px' }}>
      <div style={{ fontWeight: '500', marginBottom: '4px' }}>
        {label} {required && <span style={{ color: 'red' }}>*</span>}
      </div>
      {children}
    </div>
  );

  // Main render
 return (
    <>
      {!showForm ? (
        <Button 
          primary 
          onClick={() => setShowForm(true)} 
          icon={<IconAdd16 />}
          data-test="create-org-button"
        >
          Create your Organization
        </Button>
      ) : (
        <div 
          style={{ 
            padding: '24px',
            border: '1px solid #e0e0e0',
            borderRadius: '4px',
            backgroundColor: '#f8f8f8'
          }}
          data-test="org-form-container"
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
            <h3>New Organization Unit</h3>
            <Button 
              small 
              secondary 
              onClick={() => setShowForm(false)}
              icon={<IconFullscreenExit16 />}
            >
              Close
            </Button>
          </div>

          {notification && (
            <AlertBar
              critical={notification.critical}
              success={notification.success}
              onHidden={() => setNotification(null)}
            >
              {notification.message}
            </AlertBar>
          )}

          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              {/* Organization Section */}
              {renderFormField('Continent', (
                <SingleSelect
                  selected={formData.continentId}
                  onChange={({ selected }) => setFormData(prev => ({
                    ...prev,
                    continentId: selected,
                    countryId: '',
                    regionId: ''
                  }))}
                  loading={orgUnitsLoading}
                  filterable
                >
                  {continent.map(continent => (
                    <SingleSelectOption key={continent.id} value={continent.id} label={continent.name} />
                  ))}
                </SingleSelect>
              ))}

              {renderFormField('Country', (
                <SingleSelect
                  selected={formData.countryId}
                  onChange={({ selected }) => setFormData(prev => ({
                    ...prev,
                    countryId: selected,
                    regionId: ''
                  }))}
                  loading={orgUnitsLoading}
                  filterable
                >
                  {countries.map(country => (
                    <SingleSelectOption key={country.id} value={country.id} label={country.name} />
                  ))}
                </SingleSelect>
              ))}

              {formData.countryId && renderFormField('Region', (
                <SingleSelect
                  selected={formData.regionId}
                  onChange={({ selected }) => setFormData(prev => ({
                    ...prev,
                    regionId: selected
                  }))}
                  loading={orgUnitsLoading}
                  filterable
                >
                  {regions.map(region => (
                    <SingleSelectOption key={region.id} value={region.id} label={region.name} />
                  ))}
                </SingleSelect>
              ))}

              {formData.regionId && renderFormField('Organization Name', (
                <InputField
                  value={formData.organisationName}
                  onChange={({ value }) => setFormData(prev => ({
                    ...prev,
                    organisationName: value
                  }))}
                  required
                />
              ))}

              {formData.regionId && renderFormField('Organization Email', (
                <InputField
                  type="email"
                  value={formData.orgEmail}
                  onChange={({ value }) => setFormData(prev => ({
                    ...prev,
                    orgEmail: value
                  }))}
                  validationText="Organization contact email"
                />
              ))}

              {formData.regionId && renderFormField('Organization Type', (
                <SingleSelect
                  selected={formData.organisationType}
                  onChange={({ selected }) => setFormData(prev => ({
                    ...prev,
                    organisationType: selected
                  }))}
                  loading={orgTypesLoading}
                  required
                >
                  {orgTypes.map(type => (
                    <SingleSelectOption 
                      key={type.id} 
                      value={type.id} 
                      label={type.name} 
                    />
                  ))}
                </SingleSelect>
              ))}

              {/* Department Management */}
              {formData.organisationName && (
                <>
                  {renderFormField('Add Departments', (
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <InputField
                        value={formData.newDepartment}
                        onChange={({ value }) => setFormData(prev => ({
                          ...prev,
                          newDepartment: value
                        }))}
                        placeholder="Department name"
                      />
                      <Button onClick={addDepartment} small>
                        Add
                      </Button>
                    </div>
                  ))}

                  {formData.departments.length > 0 && (
                    <div style={{ marginBottom: '16px' }}>
                      <div style={{ fontWeight: '500', marginBottom: '4px' }}>
                        Departments
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                        {formData.departments.map((dept, index) => (
                          <Chip
                            key={index}
                            onRemove={() => removeDepartment(index)}
                          >
                            {dept}
                          </Chip>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
            <div style={{ display: 'flex', gap: '16px' }}>
              <Button 
                type="submit" 
                primary 
                disabled={submitting || orgUnitsLoading || orgTypesLoading}
                icon={submitting ? <CircularLoader small /> : null}
              >
                {submitting ? 'Creating...' : 'Create Organization'}
              </Button>
              <Button 
                secondary 
                onClick={() => {
                  setShowForm(false);
                  setFormData({
                    countryId: '',
                    regionId: '',
                    organisationName: '',
                    organisationType: '',
                    orgEmail: '',
                    departments: [],
                    newDepartment: '',
                  });
                }}
                disabled={submitting}
                style={{
                  backgroundColor: '#f39c12',
                  color: 'white',
                  ':hover': {
                    backgroundColor: '#c0392b' 
                  }
                }}
              >
                Cancel
              </Button>
            </div>
          </form>
        </div>
      )}
    </>
  );
};

export default OrgUnitForm;